import classNames from 'classnames';

/** Серый прямоугольник-заглушка. Ширина/высота задаются классами. */
const Bar = ({ className = '' }: { className?: string }) => (
  <div className={classNames('rounded-[6px] bg-[#151515]/10', className)} />
);

/**
 * Заглушка карточки текущего заказа — повторяет ClientCurrentOrderCard:
 * шапка с номером и статусом, белый блок с количеством позиций,
 * комментарием и адресом, строка «Сформировал(а) заказ».
 */
export const CurrentOrderCardSkeleton = () => (
  <div className="flex w-full animate-pulse flex-col overflow-hidden rounded-[16px] bg-[#e4f0f7] p-[6px]">
    <div className="flex items-center gap-[12px] p-[10px]">
      <Bar className="h-5 w-[92px]" />
      <Bar className="h-7 w-[104px] rounded-[100px]" />
    </div>
    <div className="flex flex-col gap-[12px] self-stretch rounded-b-[12px] rounded-t-[16px] bg-white p-[16px]">
      <div className="flex items-center gap-[8px] py-[8px]">
        <Bar className="size-6 rounded-full" />
        <Bar className="h-4 w-[88px]" />
      </div>
      <Bar className="h-4 w-2/3" />
      <Bar className="h-4 w-1/2" />
      <div className="border-t border-[#f3f4f6] pt-[16px]">
        <Bar className="h-4 w-[180px]" />
      </div>
    </div>
  </div>
);

/** Список заглушек текущих заказов. */
export const CurrentOrdersSkeleton = ({ count = 2 }: { count?: number }) => (
  <div className="flex w-full flex-col items-start gap-[20px]">
    {Array.from({ length: count }, (_, i) => (
      <CurrentOrderCardSkeleton key={i} />
    ))}
  </div>
);

/**
 * Заглушка карточки заказа — повторяет ClientOrderDetails: полоса
 * «Вернуться назад», плашка статуса, номер, комментарий и блок «Лекарства».
 */
export const OrderDetailsSkeleton = () => (
  <div className="w-full max-w-[968px] animate-pulse overflow-hidden rounded-[16px] bg-white">
    <div className="flex h-[74px] w-full items-center bg-[#f1f7fa] px-[24px]">
      <Bar className="h-6 w-[180px]" />
    </div>
    <div className="w-full overflow-hidden rounded-b-[16px] bg-white">
      <div className="flex flex-col items-start bg-[#f1f7fa]">
        <div className="flex w-full items-center px-[24px] py-[12px]">
          <Bar className="h-5 w-[150px]" />
        </div>
        <div className="flex w-full flex-col items-start gap-[16px] rounded-t-[16px] bg-white p-[24px]">
          <div className="flex h-[40px] items-center gap-[12px]">
            <Bar className="h-8 w-[128px]" />
            <Bar className="h-7 w-[110px] rounded-[100px]" />
          </div>
          <div className="w-full rounded-[12px] bg-[#f7f8f9] p-[16px]">
            <Bar className="mb-[8px] h-4 w-[104px]" />
            <Bar className="h-5 w-1/3" />
          </div>
        </div>
      </div>
      <div className="flex flex-col items-start rounded-[16px] bg-[#e4f0f7]">
        <div className="flex min-h-[52px] w-full items-center justify-between px-[24px] py-[16px]">
          <Bar className="h-5 w-[92px]" />
          <Bar className="h-5 w-[140px]" />
        </div>
        <div className="flex w-full flex-col gap-[16px] rounded-t-[16px] bg-white p-[24px]">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex w-full items-center gap-[12px]">
              <Bar className="h-5 w-[20px]" />
              <Bar className="h-5 flex-1" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/**
 * Заглушка строк истории платежей: колонки те же, что в таблице —
 * номер платежа, номер заказа, дата, статус и сумма.
 */
export const PaymentsSkeleton = ({ rows = 5 }: { rows?: number }) => (
  <tbody className="animate-pulse">
    {Array.from({ length: rows }, (_, row) => (
      <tr key={row}>
        <td className="border-none" />
        <td>
          <Bar className="h-5 w-[84px]" />
        </td>
        <td>
          <Bar className="h-5 w-[88px]" />
        </td>
        <td>
          <Bar className="h-5 w-[84px]" />
        </td>
        <td>
          <Bar className="h-5 w-[96px]" />
        </td>
        <td>
          <Bar className="h-5 w-[72px]" />
        </td>
        <td className="border-none" />
      </tr>
    ))}
  </tbody>
);

/**
 * Заглушка строк истории заказов. Шапку таблицы не подменяем — её подписи
 * известны сразу. Ширины подобраны под реальные значения: номер, дата,
 * количество лекарств, цена и плашка статуса.
 */
export const OrderHistorySkeleton = ({ rows = 5 }: { rows?: number }) => (
  <div className="flex w-full animate-pulse flex-col items-start self-stretch">
    {Array.from({ length: rows }, (_, row) => (
      <div key={row} className="w-full">
        <div className="hidden w-full items-center gap-6 px-0 py-5 md:flex">
          <div className="flex-1">
            <Bar className="h-5 w-[88px]" />
          </div>
          <div className="flex-1">
            <Bar className="h-5 w-[84px]" />
          </div>
          <div className="flex-1">
            <Bar className="h-5 w-[18px]" />
          </div>
          <div className="flex-1">
            <Bar className="h-5 w-[72px]" />
          </div>
          <div className="flex-1">
            <Bar className="h-7 w-[104px] rounded-[100px]" />
          </div>
        </div>
        <div className="flex w-full flex-col gap-[12px] py-[16px] md:hidden">
          {[
            ['w-[104px]', 'w-[88px]'],
            ['w-[92px]', 'w-[84px]'],
            ['w-[80px]', 'w-[18px]'],
            ['w-[52px]', 'w-[72px]'],
          ].map(([label, value], i) => (
            <div key={i} className="flex w-full items-center justify-between">
              <Bar className={classNames('h-4', label)} />
              <Bar className={classNames('h-4', value)} />
            </div>
          ))}
          <div className="flex w-full items-center justify-between">
            <Bar className="h-4 w-[60px]" />
            <Bar className="h-7 w-[104px] rounded-[100px]" />
          </div>
        </div>
        <div className="h-px w-full bg-[#151515]/10" />
      </div>
    ))}
  </div>
);
